import { FileText, Loader2 } from 'lucide-react'
import mammoth from 'mammoth'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { PdfPageStack } from '@/components/documents/PdfPageStack'
import { AudioPlayer } from '@/components/ui/audio-player'
import { Skeleton } from '@/components/ui/skeleton'
import { HUMINT_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import { useIsMobile } from '@/hooks/use-mobile'
import { apiClient } from '@/services/api/client'
import { isAudioFileType } from '@/utils/audio-playback'
import { parseCsvPreview } from '@/utils/csv-preview'
import { previewBlob, previewDownloadPath } from './document-preview-source'

// XLSX preview is code-split: the ~1 MB SheetJS lib loads only when the
// user opens an XLSX/XLS document in the overlay, not on app boot.
const RecordingAudioPreview = lazy(
  () => import('@/features/recording-voices/RecordingAudioPreview')
)
const XlsxPreview = lazy(() => import('./XlsxPreview'))

const XLSX_PREVIEW_EXTS = new Set(['xlsx', 'xls'])

const TEXT_PREVIEW_EXTS = new Set(['txt', 'md', 'log', 'text'])
// Cap text preview to keep the DOM manageable on large logs (CDR exports
// can be 100+ MB). The cap is a conservative slice; a "truncated" hint is
// shown when content was clipped.
const TEXT_PREVIEW_MAX_CHARS = 500_000
const TEXT_PREVIEW_MAX_LINES = 5_000

interface CaseDocumentPreviewProps {
  caseId: string
  documentId: string
  fileType: string | null
  filename: string
  pageNumber?: number
  /** Optional 1-based page numbers to scope the PDF preview to. When set
   *  on a PDF, the preview renders only those pages (via react-pdf)
   *  with a small banner explaining the scope. Ignored for non-PDFs. */
  selectedPages?: number[] | null
}

export function CaseDocumentPreview({
  caseId,
  documentId,
  fileType,
  filename,
  pageNumber,
  selectedPages,
}: CaseDocumentPreviewProps) {
  // Read here, not in PdfPreview: it has settled by the time the file has loaded, so
  // the phone never mounts the iframe (which some phone browsers turn into a download).
  const isMobile = useIsMobile()
  const voiceMatching = useExtensionEnabled(HUMINT_EXTENSION_ID).enabled
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [csvPreviewData, setCsvPreviewData] = useState<string[][]>([])
  const [docxHtml, setDocxHtml] = useState<string | null>(null)
  const [xlsxBlob, setXlsxBlob] = useState<Blob | null>(null)
  const [textPreview, setTextPreview] = useState<{
    content: string
    totalLines: number
    truncated: boolean
  } | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    // A preview closed or switched while its file loads stops the download: a recording is
    // large, and every read of a capture's file is recorded.
    const controller = new AbortController()

    const loadDocument = async () => {
      try {
        setLoading(true)
        setError(null)
        setCsvPreviewData([])
        setDocxHtml(null)
        setXlsxBlob(null)
        setTextPreview(null)

        const token = apiClient.getToken()
        const response = await fetch(previewDownloadPath(caseId, documentId, fileType), {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.statusText}`)
        }

        const blob = await response.blob()

        const blobWithType = previewBlob(blob, fileType, response.url)

        if (fileType === 'docx') {
          // Browsers can't render .docx natively. Convert to HTML on the
          // client via mammoth — same pattern the Lake preview uses.
          try {
            const arrayBuffer = await blob.arrayBuffer()
            const result = await mammoth.convertToHtml({ arrayBuffer })
            setDocxHtml(result.value)
          } catch (convertErr) {
            console.error('[CaseDocumentPreview] DOCX conversion failed', convertErr)
          }
        }

        if (XLSX_PREVIEW_EXTS.has(fileType ?? '')) {
          // Hand the raw blob to the lazy XlsxPreview component — it parses
          // with SheetJS only after its chunk has loaded.
          setXlsxBlob(blob)
        }

        if (fileType === 'csv') {
          const text = await blob.text()
          // Quote-aware split on the auto-detected delimiter so
          // European-formatted values like `0,46326759` stay intact.
          setCsvPreviewData(parseCsvPreview(text, 20))
        }

        if (TEXT_PREVIEW_EXTS.has(fileType ?? '')) {
          const fullText = await blob.text()
          const totalLines = fullText.split('\n').length
          const charClipped = fullText.length > TEXT_PREVIEW_MAX_CHARS
          const sliced = charClipped ? fullText.slice(0, TEXT_PREVIEW_MAX_CHARS) : fullText
          const lines = sliced.split('\n')
          const lineClipped = lines.length > TEXT_PREVIEW_MAX_LINES
          const previewContent = lineClipped
            ? lines.slice(0, TEXT_PREVIEW_MAX_LINES).join('\n')
            : sliced
          setTextPreview({
            content: previewContent,
            totalLines,
            truncated: charClipped || lineClipped,
          })
        }

        if (controller.signal.aborted) return
        objectUrl = URL.createObjectURL(blobWithType)
        setDocumentUrl(objectUrl)
        setLoading(false)
      } catch (err) {
        if (controller.signal.aborted) return
        setError(err instanceof Error ? err.message : 'Failed to load document')
        setLoading(false)
      }
    }

    loadDocument()

    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [caseId, documentId, fileType])

  // NOTE: hooks must come before any early return — keep this above the
  // loading/error branches.
  const scopedPages = useMemo(() => {
    if (!selectedPages || selectedPages.length === 0) return null
    return [...new Set(selectedPages)].sort((a, b) => a - b)
  }, [selectedPages])

  if (loading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="rounded-lg border border-destructive-border bg-destructive-surface p-4 text-sm text-destructive-foreground">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 h-full flex flex-col">
      {documentUrl && fileType === 'pdf' && (
        <PdfPreview
          documentUrl={documentUrl}
          scopedPages={scopedPages}
          pageNumber={pageNumber}
          isMobile={isMobile}
        />
      )}

      {fileType === 'docx' && docxHtml && (
        <div
          className="flex-1 overflow-auto border border-border rounded bg-card p-4 prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: docxHtml }}
        />
      )}

      {fileType === 'docx' && !docxHtml && (
        <div className="border border-border rounded p-6 text-center text-muted-foreground text-sm">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Loader2 className="size-5 animate-spin opacity-60" />
            <FileText className="size-5 opacity-40" />
          </div>
          <p>Could not render this Word document.</p>
          <p className="text-xs mt-1 opacity-60">{filename}</p>
        </div>
      )}

      {documentUrl && ['jpg', 'jpeg', 'png', 'webp'].includes(fileType || '') && (
        <img
          src={documentUrl}
          alt={filename}
          className="max-w-full h-auto border border-border rounded"
        />
      )}

      {documentUrl &&
        isAudioFileType(fileType) &&
        (voiceMatching ? (
          <Suspense fallback={<AudioPlayer src={documentUrl} label={filename} />}>
            <RecordingAudioPreview
              key={documentId}
              scope={{ caseId, documentId, filename }}
              src={documentUrl}
            />
          </Suspense>
        ) : (
          <AudioPlayer src={documentUrl} label={filename} />
        ))}

      {XLSX_PREVIEW_EXTS.has(fileType ?? '') && xlsxBlob && (
        <Suspense
          fallback={
            <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
              <Loader2 className="size-4 animate-spin" />
              <span>Loading spreadsheet…</span>
            </div>
          }
        >
          <XlsxPreview blob={xlsxBlob} filename={filename} />
        </Suspense>
      )}

      {fileType === 'csv' && csvPreviewData.length > 0 && (
        <div className="border border-border rounded overflow-auto flex-1">
          <table className="w-full text-xs">
            <tbody>
              {csvPreviewData.map((row, i) => (
                <tr key={i} className={i === 0 ? 'bg-muted/50 font-medium' : ''}>
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className="border-b border-border px-3 py-1.5 text-foreground whitespace-pre-line align-top"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {csvPreviewData.length >= 20 && (
            <div className="text-xs text-muted-foreground text-center py-2 italic">
              Showing first 20 rows
            </div>
          )}
        </div>
      )}

      {TEXT_PREVIEW_EXTS.has(fileType ?? '') && textPreview && (
        <div className="border border-border rounded flex-1 overflow-auto bg-muted/20">
          <pre className="text-xs font-mono whitespace-pre p-3 text-foreground/90 leading-relaxed">
            {textPreview.content}
          </pre>
          {textPreview.truncated && (
            <div className="text-xs text-muted-foreground text-center py-2 italic border-t border-border bg-muted/40 sticky bottom-0">
              Preview truncated · showing first {TEXT_PREVIEW_MAX_LINES.toLocaleString()} lines of{' '}
              {textPreview.totalLines.toLocaleString()} total
            </div>
          )}
        </div>
      )}

      {!['pdf', 'docx', 'jpg', 'jpeg', 'png', 'webp', 'csv'].includes(fileType || '') &&
        !TEXT_PREVIEW_EXTS.has(fileType ?? '') &&
        !isAudioFileType(fileType) &&
        !XLSX_PREVIEW_EXTS.has(fileType ?? '') && (
          <div className="border border-border rounded p-6 text-center text-muted-foreground text-sm">
            <FileText className="size-8 mx-auto mb-2 opacity-40" />
            <p>Preview not available for this file type.</p>
            <p className="text-xs mt-1 opacity-60">{filename}</p>
          </div>
        )}
    </div>
  )
}

interface PdfPreviewProps {
  documentUrl: string
  scopedPages: number[] | null
  pageNumber?: number
  isMobile: boolean
}

function PdfPreview({ documentUrl, scopedPages, pageNumber, isMobile }: PdfPreviewProps) {
  if (scopedPages) return <ScopedPdfPreview documentUrl={documentUrl} pages={scopedPages} />
  // Phone browsers cannot show a PDF inside an iframe, so the pages are drawn here.
  if (isMobile) return <PdfPageStack documentUrl={documentUrl} className="min-h-0" />
  return (
    <iframe
      src={documentUrl + (pageNumber ? `#page=${pageNumber}` : '')}
      className="flex-1 w-full min-h-[500px] border border-border rounded"
      title="Document preview"
    />
  )
}

// ─── Scoped PDF preview ─────────────────────────────────────────────────────
//
// Renders only the page numbers in `pages` (via PdfPageStack). Used when the
// case_document has an import_scope with scope_type='pages' — the user sees
// exactly the slice that will go to extraction. The underlying file in S3
// is still the full PDF; clipping happens at the pipeline read step.

function ScopedPdfPreview({ documentUrl, pages }: { documentUrl: string; pages: number[] }) {
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="mb-2 rounded border border-warning-border bg-warning-surface px-3 py-2 text-xs text-warning-foreground">
        Showing {pages.length} of the source document's pages — {formatPagesShort(pages)}. The full
        file is still stored; extraction uses only this slice.
      </div>
      <PdfPageStack documentUrl={documentUrl} pages={pages} />
    </div>
  )
}

function formatPagesShort(pages: number[]): string {
  if (!pages.length) return ''
  const sorted = [...pages].sort((a, b) => a - b)
  const runs: string[] = []
  let start = sorted[0]
  let prev = sorted[0]
  for (let i = 1; i < sorted.length; i++) {
    const n = sorted[i]
    if (n === prev + 1) {
      prev = n
      continue
    }
    runs.push(start === prev ? `${start}` : `${start}-${prev}`)
    start = n
    prev = n
  }
  runs.push(start === prev ? `${start}` : `${start}-${prev}`)
  return `pages ${runs.join(', ')}`
}
